<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $, Alert } from '@bagelink/vue'
const schema = defineSchema({
	useDatastoreData: $.checkbox('Use authors from datastore', { default: true }),
	headerText: $.text('Header Text', { default: 'Torah with Academic Biblical Scholarship:' }),
	browseLabel: $.text('Browse Label', { default: 'Browse Our Authors' }),
	browseLink: $.text('Browse Link', { default: '/authors' }),
	datastoreExplanation: $.display(Alert, {
		info: true,
		thin: true,
		title: 'Authors are loaded automatically',
		message:
			'The author list and total count come from the datastore. Disable “Use authors from datastore” to enter authors manually.',
	}).if('useDatastoreData eq true'),
	authors: $.array(
		'Authors',
		defineSchema({
			title: $.text('Title'),
			name: $.text('Name'),
			academy: $.text('Academy'),
			image: $.upload('Image'),
		}),
		{ collapsible: true },
	).if('useDatastoreData eq false'),
})
export const BlockAuthorsBanner = defineBlock({
	thumbnail: '/block-thumbs/BlockAuthorsBanner.svg',
	category: 'Hero',
	order: 150,
	name: 'BlockAuthorsBanner',
	label: 'Authors Banner',
	icon: 'groups',
	schema,
	defaults: {
		useDatastoreData: true,
		headerText: 'Torah with Academic Biblical Scholarship:',
		browseLabel: 'Browse Our Authors',
		browseLink: '/authors',
		authors: [],
	},
	dataSource: { collection: 'authors', store: 'the_torah', label: 'Authors' },
})
</script>
<script setup lang="ts">
import { useBloxRuntime } from '@bagelink/blox'
import { Icon, Image, Skeleton, Swiper } from '@bagelink/vue'
import { computed } from 'vue'
import { useCollection } from '@/composables/useDatastore'



const props = defineProps<{
	useDatastoreData?: boolean
	headerText?: string
	browseLabel?: string
	browseLink?: string
	authors?: { title: string; name: string; academy: string; image: string }[]
}>()

const PREVIEW_AUTHORS = [
	{ title: 'Prof.', name: 'John Doe', academy: 'University Faculty', image: '/og.jpg' },
	{ title: 'Dr.', name: 'Sarah Cohen', academy: 'Biblical Studies', image: '/webclip.png' },
]

const { isPreview } = useBloxRuntime()
const { items: datastoreAuthors, loading: authorsLoading } = useCollection('authors', {
	limit: 50,
	pick: ['id', '_id', 'name', 'title', 'image', 'slug', 'institution', 'hidden'],
})
const { items: countedAuthors } = useCollection('authors', {
	all: true,
	limit: 1000,
	pick: ['_id', 'hidden'],
})
const authorsTotal = computed(
	() =>
		countedAuthors.value.filter((author: { hidden?: boolean }) => author.hidden !== true).length,
)
const authorsWithImages = computed(() => {
	if (props.useDatastoreData === false) return props.authors ?? []
	const authors = datastoreAuthors.value
		.filter((author: { hidden?: boolean }) => author.hidden !== true)
		.map((author: any) => ({
			title: author.title || '',
			name: author.name || '',
			slug: author.slug || '',
			academy: author.institution || '',
			image: author.image || '',
		}))
	if (authors.length || authorsLoading.value || !isPreview.value) return authors
	return PREVIEW_AUTHORS
})
const showSkeleton = computed(
	() => props.useDatastoreData !== false && authorsLoading.value && !authorsWithImages.value.length,
)
const browseAuthorsLabel = computed(() => {
	const label = (props.browseLabel || 'Browse Our Authors')
		.replace(/\{count\}|\b\d[\d,]*\b/gi, '')
		.replace(/\s+/g, ' ')
		.trim()
	const count = props.useDatastoreData === false ? (props.authors?.length ?? 0) : authorsTotal.value
	if (!count) return label
	const formattedCount = count.toLocaleString('en-US')
	return /\bauthors\b/i.test(label)
		? label.replace(/\bauthors\b/i, `${formattedCount} Authors`)
		: `${label} ${formattedCount}`
})
</script>

<template>
	<div class="w1200px py-2 testMe1 m_pb-1">
		<!-- Header -->
		<div class="txt17 m_txt15 txt-center flex gap-col-05 justify-content-center flex-wrap">
			<img src="@/assets/torahbook.svg" alt="thetorah Authors" />
			<i>
				{{ headerText }}
			</i>
			<router-link :to="browseLink || '/authors'" class="color-primary decoration-none">
				{{ browseAuthorsLabel }}
			</router-link>
		</div>

		<div class="py-1 m_none">
			<div
				v-if="showSkeleton"
				class="grid grid-wrap-7 px-3"
				role="status"
				aria-label="Loading authors"
				aria-busy="true"
			>
				<div
					v-for="author in 7"
					:key="author"
					class="grid justify-items-center gap-05"
					aria-hidden="true"
				>
					<Skeleton width="45px" height="45px" class="skeleton-round" />
					<Skeleton width="85%" height="18px" />
					<Skeleton width="65%" height="13px" />
				</div>
			</div>
			<Swiper
				v-else-if="authorsWithImages.length"
				:items="authorsWithImages"
				:slides-per-view="7"
				:loop="true"
				:advanced-options="{ slidesPerGroup: 7 }"
				class="px-3"
			>
				<template #default="{ item: author }">
					<div class="txt-center">
						<router-link
							:to="(author as any).slug ? `/author/${(author as any).slug}` : '/authors'"
							class="txt-center decoration-none"
						>
							<Image
								:src="(author as any).image"
								:alt="(author as any).name"
								ratio="1"
								class="profile45 mx-auto mb-05 border"
							/>
							<i class="txt15 mb-025 color-black block">
								{{ (author as any).title }} {{ (author as any).name }}
							</i>
							<p class="txt11 color-gray-70">
								{{ (author as any).academy }}
							</p>
						</router-link>
					</div>
				</template>
				<template #navigation="{ prev, next }">
					<Icon
						name="single_arrow"
						class="user-select-none hover z-99 absolute top-3 color-primary start line-height-05 rotate-180"
						:size="4"
						:weight="100"
						@click="prev()"
					/>
					<Icon
						name="single_arrow"
						class="user-select-none hover z-99 absolute top-3 color-primary end line-height-05"
						:size="4"
						:weight="100"
						@click="next()"
					/>
				</template>
			</Swiper>
		</div>
	</div>
</template>

<style>
.profile45 {
	width: 45px;
	height: 45px;
	border-radius: 50%;
	object-fit: cover;
}
.skeleton-round {
	--bgl-skeleton-radius: 50% !important;
}
</style>
